<div class="container mx-auto px-4 py-6 max-w-7xl">

<div class="bg-white shadow-md rounded-lg p-6">

  <!-- Header -->
  <div class="flex justify-between items-center mb-6">
    <div class="flex items-center space-x-2">
      <button mat-icon-button (click)="goBack()" class="text-gray-600 hover:text-gray-800">
        <mat-icon>arrow_back</mat-icon>
      </button>
      <h2 class="text-2xl font-bold mb-0">
        {{ 'APPOINTMENT_EDIT.TITLE' | translate }}: 
        <span *ngIf="kontrolltermin">#{{ kontrolltermin.einsatznummer }}</span>
      </h2>
    </div>
    <div class="flex items-center space-x-2">
      <span 
        class="inline-flex items-center px-4 py-1.5 rounded-md text-sm font-semibold shadow-sm"
        [style.background-color]="getStatusHeaderColor()"
        [style.color]="sourceStatus === 'bearbeitung' ? '#212529' : '#ffffff'">
        {{ getStatusHeaderLabel() }}
      </span>
    </div>
  </div>

  <!-- Loading State -->
  <div *ngIf="isLoading" class="flex justify-center items-center py-8">
    <div class="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600"></div>
    <span class="ml-3 text-gray-600 text-base">{{ 'APPOINTMENT_EDIT.LOADING' | translate }}</span>
  </div>

  <!-- Error State -->
  <div *ngIf="!isLoading && !kontrolltermin" class="flex flex-col items-center justify-center py-12">
    <div class="text-center">
      <mat-icon class="text-6xl text-gray-400 mb-4">error_outline</mat-icon>
      <h3 class="text-xl font-semibold text-gray-700 mb-2">{{ 'APPOINTMENT_EDIT.NOT_FOUND' | translate }}</h3>
      <p class="text-gray-500 mb-6">{{ 'APPOINTMENT_EDIT.NOT_FOUND_MSG' | translate }}</p>
      <button 
        mat-raised-button 
        color="primary" 
        (click)="goBack()">
        <mat-icon class="mr-2">arrow_back</mat-icon>
        {{ 'APPOINTMENT_EDIT.BACK_TO_LIST' | translate }}
      </button>
    </div>
  </div>

  <!-- Content -->
  <div *ngIf="!isLoading && kontrolltermin" class="space-y-4">

    <!-- Bemerkung Zutritt Kontakt (Standort key remark) - informational, shown across all statuses -->
    <div *ngIf="kontrolltermin.standort?.bemerkung_struktur_anmerkung"
         class="p-4 bg-amber-50 border border-amber-200 rounded-lg">
      <p class="text-xs font-semibold text-amber-800 mb-1 flex items-center gap-1">
        <mat-icon class="w-4 h-4 text-base leading-none">vpn_key</mat-icon> Bemerkung Zutritt Kontakt
      </p>
      <p class="text-sm text-gray-800 whitespace-pre-line break-words">{{ kontrolltermin.standort?.bemerkung_struktur_anmerkung }}</p>
    </div>

    <!-- Status-Specific Views -->
    <div *ngIf="sourceStatus === 'geloescht'">
      <app-geloescht-view 
        [kontrolltermin]="kontrolltermin" 
        [isEditMode]="isEditMode"
        (statusChanged)="onStatusChanged($event)">
      </app-geloescht-view>
    </div>
    
    <div *ngIf="sourceStatus === 'abgebrochen'">
      <app-abgebrochen-view 
        [kontrolltermin]="kontrolltermin" 
        [isEditMode]="isEditMode"
        (statusChanged)="onStatusChanged($event)">
      </app-abgebrochen-view>
    </div>
    
    <div *ngIf="sourceStatus === 'fehlerhaft'">
      <app-fehlerhaft-view 
        [kontrolltermin]="kontrolltermin" 
        [isEditMode]="isEditMode"
        (statusChanged)="onStatusChanged($event)">
      </app-fehlerhaft-view>
    </div>
    
    <div *ngIf="sourceStatus === 'geplant'">
      <app-geplant-view 
        [kontrolltermin]="kontrolltermin" 
        [isEditMode]="isEditMode"
        (statusChanged)="onStatusChanged($event)">
      </app-geplant-view>
    </div>
    
    <!-- Default view for other statuses -->
    <div *ngIf="!['geloescht', 'abgebrochen', 'fehlerhaft', 'geplant'].includes(sourceStatus)">
    
    <!-- Header Component for Bearbeitung, Freigabe and Abgeschlossen -->
    <div *ngIf="sourceStatus === 'bearbeitung' || sourceStatus === 'freigabe' || sourceStatus === 'abgeschlossen'">
      <app-header-info 
        [kontrolltermin]="kontrolltermin" 
        [editableFields]="getEditableFields()"
        [showTimeFields]="true"
        [sourceStatus]="sourceStatus"
        (batchFieldsChanged)="onHeaderBatchChanged($event)">
      </app-header-info>

      <!-- Section B: Object Structure with Indicators Table -->
      <div class="mb-4 p-4 bg-gray-50 border border-gray-200 rounded-lg">
        <div class="flex items-center justify-between gap-x-6">
          <div class="min-w-0 flex-1">
            <div class="flex items-start gap-x-3">
              <p class="text-sm font-semibold text-gray-900 dark:text-white">{{ 'APPOINTMENT_EDIT.SECTION_STRUCTURE' | translate }}</p>
              <span class="inline-flex items-center rounded-md bg-green-50 px-2 py-1 text-xs font-medium text-green-700 ring-1 ring-inset ring-green-600/20 dark:bg-green-400/10 dark:text-green-400 dark:ring-green-500/20">
                {{ kontrolltermin.kontrollIndikatoren?.length || 0 }} {{ 'INDICATOR.TITLE' | translate }}
              </span>
            </div>
          </div>
        </div>

        <div *ngIf="kontrolltermin.kontrollIndikatoren && kontrolltermin.kontrollIndikatoren.length > 0; else noIndicators" 
             class="mt-4 overflow-x-auto">
          <table class="w-full table-fixed divide-y divide-gray-200">
            <thead class="bg-gray-100">
              <tr>
                <th class="px-2 py-2 text-left text-xs font-medium text-gray-500 uppercase tracking-wider w-[60px]">{{ 'APPOINTMENT_EDIT.COL_NR' | translate }}</th>
                <th class="px-2 py-2 text-left text-xs font-medium text-gray-500 uppercase tracking-wider w-[15%]">{{ 'APPOINTMENT_EDIT.COL_OBJECT' | translate }}</th>
                <th class="px-2 py-2 text-left text-xs font-medium text-gray-500 uppercase tracking-wider w-[18%]">{{ 'APPOINTMENT_EDIT.COL_INDICATOR' | translate }}</th>
                <th class="px-2 py-2 text-left text-xs font-medium text-gray-500 uppercase tracking-wider w-[8%]">{{ 'APPOINTMENT_EDIT.COL_STATUS' | translate }}</th>
                <th class="px-2 py-2 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">{{ 'APPOINTMENT_EDIT.COL_MEASURE' | translate }}</th>
                <th *ngIf="sourceStatus !== 'bearbeitung'" class="px-2 py-2 text-left text-xs font-medium text-gray-500 uppercase tracking-wider w-[10%]">{{ 'APPOINTMENT_EDIT.COL_CHECKED' | translate }}</th>
                <!-- Anmerkung column removed; shown as icon in Maßnahme cell -->
                <th class="px-2 py-2 text-left text-xs font-medium text-gray-500 uppercase tracking-wider w-[80px]">{{ 'APPOINTMENT_EDIT.COL_ACTIONS' | translate }}</th>
              </tr>
            </thead>
            <tbody class="bg-white divide-y divide-gray-200">
              <tr *ngFor="let indikator of kontrolltermin.kontrollIndikatoren; let i = index" 
                  class="hover:bg-gray-50 cursor-pointer"
                  (click)="editIndikator(indikator, $event)">
                <!-- Nummer Badge -->
                <td class="px-2 py-1.5 whitespace-nowrap">
                  <span class="inline-flex items-center rounded-md bg-purple-50 px-1.5 py-0.5 text-xs font-medium text-purple-700 ring-1 ring-inset ring-purple-600/20">
                    {{ getIndikatorDisplayNumber(indikator) }}
                  </span>
                </td>
                <!-- Objekt Info -->
                <td class="px-2 py-1.5">
                  <div class="text-xs truncate">
                    <div class="font-medium text-gray-900 truncate">{{ indikator.gebaeude_name }}</div>
                    <div class="text-gray-500 truncate">{{ indikator.sektion_name }} - {{ indikator.room_name }}</div>
                  </div>
                </td>

                <!-- Indikator Info -->
                <td class="px-2 py-1.5">
                  <div class="text-xs flex items-center gap-1">
                    <ng-container *ngIf="getSymbolUrlFromIndicator(indikator) as sym">
                      <div *ngIf="getSymbolColorFromIndicator(indikator) as color" 
                           class="inline-flex items-center justify-center h-5 w-5"
                           [style.background-color]="color"
                           [style.mask-image]="'url(' + sym + ')'"
                           [style.mask-size]="'contain'"
                           [style.mask-repeat]="'no-repeat'"
                           [style.mask-position]="'center'"
                           [style.-webkit-mask-image]="'url(' + sym + ')'"
                           [style.-webkit-mask-size]="'contain'"
                           [style.-webkit-mask-repeat]="'no-repeat'"
                           [style.-webkit-mask-position]="'center'">
                      </div>
                      <img *ngIf="!getSymbolColorFromIndicator(indikator)" 
                           [src]="sym" 
                           alt="Symbol" 
                           class="h-5 w-5 object-contain" />
                    </ng-container>
                    <div>
                      <div class="font-medium text-gray-900 flex items-center gap-1">
                        <span>{{ indikator.indikator_name }}</span>
                        <!-- Bluetooth icon if scanned -->
                        <mat-icon 
                          *ngIf="isIndikatorScanned(indikator)"
                          [matTooltip]="getScanTooltipText(indikator)"
                          class="!text-[14px] !w-[14px] !h-[14px] text-blue-600"
                          style="font-size: 14px; width: 14px; height: 14px;">
                          bluetooth
                        </mat-icon>
                      </div>
                      <!-- Typ/Code information -->
                       
                      <div class="text-gray-500 text-xs font-semibold" *ngIf="getIndikatorCode(indikator)">
                        {{ getIndikatorCode(indikator) }}
                      </div>
                      <div class="text-gray-500">SW: {{ getFormattedSchwellenwert(indikator) }} {{ indikator.indikator?.einheit || '' }}</div>
                    </div>
                  </div>
                </td>

                <!-- Status Column -->
                <td class="px-2 py-1.5">
                  <div class="space-y-1">
                    <!-- Show status badge when not checked -->
                    <div class="text-xs" *ngIf="!isIndikatorChecked(indikator)">
                      <span class="text-gray-500">-</span>
                    </div>
                    <!-- Show detailed status when checked -->
                    <ng-container *ngIf="isIndikatorChecked(indikator)">
                      <!-- Status Badge (OK/Warnung/Kritisch) -->
                      <div class="text-xs">
                        <span class="inline-flex items-center px-1.5 py-0.5 rounded-full text-xs font-medium text-white"
                              [ngClass]="getStatusBadgeClass(getComputedStatusId(indikator))">
                          {{ getStatusBadgeText(getComputedStatusId(indikator)) }}
                        </span>
                      </div>
                      <!-- Befall (value with unit) -->
                      <div class="text-xs">
                        <span class="font-medium text-gray-700">{{ 'APPOINTMENT_EDIT.INFESTATION' | translate }}:</span>
                        <span class="text-gray-900 ml-1">{{ getFormattedWert(indikator.wert) }} {{ indikator.indikator?.einheit || '' }}</span>
                      </div>
                    </ng-container>
                  </div>
                </td>

                <!-- Combined Problem / Maßnahme / Produkt -->
                <td class="px-2 py-1.5">
                  <div class="space-y-2">
                    <!-- Problem -->
                    <div class="text-xs" *ngIf="false">
                      <span class="font-medium text-gray-700">{{ 'COMMON.PROBLEM' | translate }}:</span>
                      <div *ngIf="getProblemItems(indikator).length > 0" class="flex flex-wrap gap-1 mt-1">
                        <span *ngFor="let item of getProblemItems(indikator)" 
                              class="inline-block px-2 py-1 bg-red-100 text-red-800 text-xs rounded">
                          {{ item.name }}
                        </span>
                      </div>
                      <span *ngIf="getProblemItems(indikator).length === 0" class="text-xs text-gray-500 ml-1">-</span>
                    </div>
                    
                    <!-- Maßnahme (show only when checked) -->
                    <div class="text-xs" *ngIf="isIndikatorChecked(indikator)">
                      <div *ngIf="getMassnahmeItems(indikator).length > 0" class="flex flex-wrap gap-1 mb-2">
                        <span *ngFor="let item of getMassnahmeItems(indikator)" 
                              class="inline-block px-2 py-1 bg-blue-100 text-blue-800 text-xs rounded">
                          {{ item.name }}
                        </span>
                      </div>
                      <span *ngIf="getMassnahmeItems(indikator).length === 0" class="text-xs text-gray-500 mb-2 block">-</span>
                      
                      <!-- Produkt -->
                      <div *ngIf="getArtikelItems(indikator).length > 0" class="flex flex-wrap gap-1">
                        <span *ngFor="let item of getArtikelItems(indikator)" 
                              class="inline-block px-2 py-1 bg-gray-100 text-gray-800 text-xs rounded">
                          {{ item.name }}<span *ngIf="item.amount && item.amount > 1"> ({{ item.amount }})</span>
                        </span>
                      </div>
                      <span *ngIf="getArtikelItems(indikator).length === 0" class="text-xs text-gray-500">-</span>
                      
                      <!-- Images -->
                      <div *ngIf="getImagesForIndikator(indikator.id).length > 0" class="flex flex-wrap gap-1 mt-1">
                        <img 
                          *ngFor="let image of getImagesForIndikator(indikator.id).slice(0, 3)"
                          [src]="getImageUrl(image)"
                          [alt]="image.original_filename"
                          class="w-8 h-8 object-cover rounded cursor-pointer border border-gray-300"
                          (click)="openImageModal(image, indikator.id); $event.stopPropagation()"
                          (error)="onImageError($event, image)"
                          (error)="onImageError($event, image)"
                          title="{{ image.original_filename }}">
                        <span *ngIf="getImagesForIndikator(indikator.id).length > 3" 
                              class="text-xs text-gray-500 inline-flex items-center px-1">
                          +{{ getImagesForIndikator(indikator.id).length - 3 }}
                        </span>
                      </div>
                      
                      <!-- Anmerkung icon with tooltip -->
                      <span *ngIf="indikator.anmerkung" class="ml-2" [matTooltip]="indikator.anmerkung">
                        <mat-icon class="!text-[16px] text-gray-500 align-middle">sticky_note_2</mat-icon>
                      </span>
                    </div>
                  </div>
                </td>

                <!-- Geprüft (for non-bearbeitung status) -->
                <td *ngIf="sourceStatus !== 'bearbeitung'" class="px-2 py-1.5 whitespace-nowrap">
                  <div class="text-xs" *ngIf="isIndikatorChecked(indikator)">
                    <div class="text-gray-900">{{ indikator.checked | date:'dd.MM.yy' }}</div>
                    <div class="text-gray-500 truncate">{{ getCheckedByDisplayName(indikator) }}</div>
                  </div>
                  <div class="text-xs text-gray-400 italic" *ngIf="!isIndikatorChecked(indikator)">
                    {{ 'APPOINTMENT_EDIT.NOT_CHECKED' | translate }}
                  </div>
                </td>


                <!-- Action Buttons -->
                <td class="px-2 py-1.5 whitespace-nowrap text-right text-sm font-medium">
                  <div class="flex items-center justify-end gap-1">
                    <button 
                      *ngIf="sourceStatus === 'bearbeitung' || sourceStatus === 'freigabe'"
                      type="button"
                      (click)="setKeinBefall(indikator, $event)"
                      [disabled]="indikator._kbSaving"
                      [matTooltip]="'APPOINTMENT_EDIT.NO_INFESTATION' | translate"
                      class="px-2 py-1 text-xs font-semibold rounded-md shadow-sm ring-1 ring-inset ring-gray-300 bg-white hover:bg-gray-200 dark:ring-white/5 dark:hover:bg-white/20 transition-colors min-w-[32px] text-center dark:bg-white/10 dark:text-white disabled:opacity-40 disabled:cursor-default"
                      [style.color]="'#1f2937'">
                      {{ indikator._kbSaving ? '...' : 'KB' }}
                    </button>
                    <button 
                      (click)="editIndikator(indikator, $event)"
                      mat-icon-button
                      color="primary"
                      class="text-blue-600 hover:text-blue-900"
                        [title]="'INDICATOR.EDIT' | translate">
                      <mat-icon class="w-4 h-4">edit</mat-icon>
                    </button>
                  </div>
                </td>
              </tr>
            </tbody>
          </table>
        </div>

        <ng-template #noIndicators>
          <div class="text-center py-6">
            <svg class="mx-auto h-8 w-8 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M9 19v-6a2 2 0 00-2-2H5a2 2 0 00-2 2v6a2 2 0 002 2h2a2 2 0 002-2zm0 0V9a2 2 0 012-2h2a2 2 0 012 2v10m-6 0a2 2 0 002 2h2a2 2 0 002-2m0 0V5a2 2 0 012-2h2a2 2 0 012 2v14a2 2 0 01-2 2h-2a2 2 0 01-2-2z"></path>
            </svg>
            <h3 class="mt-2 text-sm font-medium text-gray-900">{{ 'APPOINTMENT_EDIT.NO_INDICATORS' | translate }}</h3>
            <p class="mt-1 text-xs text-gray-500">{{ 'APPOINTMENT_EDIT.NO_INDICATORS' | translate }}</p>
          </div>
        </ng-template>
      </div>

      <!-- For abgeschlossen and freigabe views: 2-column layout (Signature + Zusätzliche Informationen) -->
      <div *ngIf="sourceStatus === 'abgeschlossen' || sourceStatus === 'freigabe'" class="mb-4 grid grid-cols-1 lg:grid-cols-2 gap-4">
        <!-- Column 1: Signature Card -->
        <div *ngIf="kontrolltermin?.customer_signature" 
             class="p-4 bg-gray-50 border border-gray-200 rounded-lg">
          <h4 class="text-lg font-semibold text-gray-800 mb-3">{{ 'APPOINTMENT_EDIT.SIGN_CUSTOMER_TITLE' | translate }}</h4>
          <div class="bg-white rounded-lg p-4 border border-gray-200">
            <div class="signature-image-container mb-3" style="display: flex; justify-content: center;">
              <img 
                [src]="kontrolltermin.customer_signature" 
                [alt]="'APPOINTMENT_EDIT.SIGN_CUSTOMER_TITLE' | translate"
                class="border border-gray-300 rounded"
                style="width: 25%; height: auto; max-width: 25%; max-height: 200px; object-fit: contain;"
                (error)="onSignatureImageError($event)">
            </div>
            <div class="signature-info text-sm text-gray-700">
              <p *ngIf="kontrolltermin.signature_name" class="mb-2">
                <strong class="text-gray-900">{{ 'COMMON.NAME' | translate }}:</strong> {{ kontrolltermin.signature_name }}
              </p>
              <p *ngIf="kontrolltermin.signature_date" class="mb-2">
                <strong class="text-gray-900">{{ 'COMMON.DATE' | translate }}:</strong> {{ getFormattedSignatureDate() }}
              </p>
            </div>
          </div>
        </div>

        <!-- Column 2: Zusätzliche Informationen Card -->
        <div class="p-4 bg-gray-50 border border-gray-200 rounded-lg" [formGroup]="kontrollterminForm">
          <div class="flex items-center justify-between gap-x-6">
            <div class="min-w-0 flex-1">
              <div class="flex items-start gap-x-3">
                <p class="text-sm font-semibold text-gray-900 dark:text-white">{{ 'APPOINTMENT_EDIT.SECTION_ADDITIONAL' | translate }}</p>
              </div>
            </div>
            <!-- Bearbeiten button for freigabe view -->
            <div *ngIf="sourceStatus === 'freigabe' && !isFreigabeEditMode">
              <button
                (click)="toggleFreigabeEditMode()"
                mat-stroked-button
                color="primary"
                class="text-xs">
                <mat-icon class="w-4 h-4 mr-1">edit</mat-icon>
                {{ 'COMMON.EDIT' | translate }}
              </button>
            </div>
          </div>

          <div class="mt-4 grid grid-cols-1 md:grid-cols-2 gap-4">
            <!-- Anmerkungen -->
            <div>
              <label class="block text-xs font-medium text-gray-700 mb-1">{{ 'APPOINTMENT_EDIT.FIELD_NOTES' | translate }}</label>
              <div
                *ngIf="sourceStatus === 'abgeschlossen'"
                class="w-full px-2 py-1.5 text-sm border border-gray-300 rounded bg-gray-50 text-gray-700 whitespace-pre-line break-words min-h-[2.5rem]">{{ kontrolltermin.anmerkungen || '-' }}</div>
              <div
                *ngIf="sourceStatus === 'freigabe' && !isFreigabeEditMode"
                class="w-full px-2 py-1.5 text-sm border border-gray-300 rounded bg-gray-50 text-gray-700 whitespace-pre-line break-words min-h-[2.5rem]">{{ kontrolltermin.anmerkungen || '-' }}</div>
              <div class="flex gap-2 mb-1"
                   *ngIf="sourceStatus === 'freigabe' && isFreigabeEditMode && textTemplates.length">
                <select [(ngModel)]="selectedTemplateId" [ngModelOptions]="{standalone: true}"
                        class="flex-1 px-2 py-1 text-sm border border-gray-300 rounded focus:ring-1 focus:ring-blue-500 focus:border-blue-500">
                  <option [ngValue]="null">{{ 'APPOINTMENT_EDIT.SELECT_TEXT_TEMPLATE' | translate }}</option>
                  <option *ngFor="let t of textTemplates" [ngValue]="t.id">{{ t.schluesselwort }}</option>
                </select>
                <button type="button" (click)="insertTemplateIntoAnmerkungen()" [disabled]="!selectedTemplateId"
                        class="px-3 py-1 text-sm bg-blue-600 hover:bg-blue-700 text-white rounded disabled:opacity-50">
                  {{ 'APPOINTMENT_EDIT.INSERT_TEXT_TEMPLATE' | translate }}
                </button>
              </div>
              <textarea
                *ngIf="sourceStatus === 'freigabe' && isFreigabeEditMode"
                formControlName="anmerkungen"
                rows="4"
                [placeholder]="'APPOINTMENT_EDIT.PLACEHOLDER_NOTES' | translate"
                class="w-full px-2 py-1.5 text-sm border border-gray-300 rounded focus:ring-1 focus:ring-blue-500 focus:border-blue-500 resize-y"></textarea>
            </div>

            <!-- Zusatz-Maßnahme -->
            <div>
              <label class="block text-xs font-medium text-gray-700 mb-1">{{ 'APPOINTMENT_EDIT.FIELD_ADDITIONAL_MEASURE' | translate }}</label>
              <div
                *ngIf="sourceStatus === 'abgeschlossen'"
                class="w-full px-2 py-1.5 text-sm border border-gray-300 rounded bg-gray-50 text-gray-700 whitespace-pre-line break-words min-h-[2.5rem]">{{ kontrolltermin.zusatz_massnahme || '-' }}</div>
              <div
                *ngIf="sourceStatus === 'freigabe' && !isFreigabeEditMode"
                class="w-full px-2 py-1.5 text-sm border border-gray-300 rounded bg-gray-50 text-gray-700 whitespace-pre-line break-words min-h-[2.5rem]">{{ kontrolltermin.zusatz_massnahme || '-' }}</div>
              <textarea
                *ngIf="sourceStatus === 'freigabe' && isFreigabeEditMode"
                formControlName="zusatz_massnahme"
                rows="4"
                [placeholder]="'APPOINTMENT_EDIT.PLACEHOLDER_ADDITIONAL_MEASURE' | translate"
                class="w-full px-2 py-1.5 text-sm border border-gray-300 rounded focus:ring-1 focus:ring-blue-500 focus:border-blue-500 resize-y"></textarea>
            </div>
          </div>

          <!-- Freigabe Edit Mode Buttons -->
          <div *ngIf="sourceStatus === 'freigabe' && isFreigabeEditMode" class="mt-4 flex justify-end gap-2">
            <button
              (click)="cancelFreigabeEdit()"
              mat-stroked-button
              [disabled]="isSaving">
              {{ 'COMMON.CANCEL' | translate }}
            </button>
            <button
              (click)="saveFreigabeEdits()"
              mat-raised-button
              color="primary"
              [disabled]="isSaving || !kontrollterminForm.valid">
              <mat-icon class="w-4 h-4 mr-1">save</mat-icon>
              {{ 'COMMON.SAVE' | translate }}
            </button>
          </div>

          <!-- Eingesetzte Produkte Button -->
          <div class="mt-4">
            <button 
              mat-raised-button 
              color="primary"
              (click)="openEingesetzteProdukteDialog()"
              class="w-full">
              <mat-icon class="w-4 h-4 mr-1">inventory_2</mat-icon>
              {{ 'APPOINTMENT_EDIT.USED_PRODUCTS' | translate }}
            </button>
          </div>
        </div>
      </div>

      <!-- For bearbeitung view: Original single-column layout -->
      <div *ngIf="sourceStatus === 'bearbeitung' || sourceStatus === 'in-bearbeitung'">
        <!-- Signature Display (for freigabe status) -->
        <div *ngIf="kontrolltermin?.customer_signature" 
             class="mb-4 p-4 bg-gray-50 border border-gray-200 rounded-lg">
          <h4 class="text-lg font-semibold text-gray-800 mb-3">{{ 'APPOINTMENT_EDIT.SIGN_CUSTOMER_TITLE' | translate }}</h4>
          <div class="bg-white rounded-lg p-4 border border-gray-200">
            <div class="signature-image-container mb-3" style="display: flex; justify-content: center;">
              <img 
                [src]="kontrolltermin.customer_signature" 
                [alt]="'APPOINTMENT_EDIT.SIGN_CUSTOMER_TITLE' | translate"
                class="border border-gray-300 rounded"
                style="width: 25%; height: auto; max-width: 25%; max-height: 200px; object-fit: contain;"
                (error)="onSignatureImageError($event)">
            </div>
            <div class="signature-info text-sm text-gray-700">
              <p *ngIf="kontrolltermin.signature_name" class="mb-2">
                <strong class="text-gray-900">{{ 'COMMON.NAME' | translate }}:</strong> {{ kontrolltermin.signature_name }}
              </p>
              <p *ngIf="kontrolltermin.signature_date" class="mb-2">
                <strong class="text-gray-900">{{ 'COMMON.DATE' | translate }}:</strong> {{ getFormattedSignatureDate() }}
              </p>
            </div>
          </div>
        </div>

        <!-- Section C: Additional Comments (Anmerkungen & Zusatz-Maßnahme) -->
        <div class="mb-4 p-4 bg-gray-50 border border-gray-200 rounded-lg" [formGroup]="kontrollterminForm">
          <div class="flex items-center justify-between gap-x-6">
            <div class="min-w-0 flex-1">
              <div class="flex items-start gap-x-3">
                <p class="text-sm font-semibold text-gray-900 dark:text-white">{{ 'APPOINTMENT_EDIT.SECTION_ADDITIONAL' | translate }}</p>
              </div>
            </div>
            <!-- Bearbeiten button for freigabe view -->
            <div *ngIf="!isFreigabeEditMode">
              <button
                (click)="toggleFreigabeEditMode()"
                mat-stroked-button
                color="primary"
                class="text-xs">
                <mat-icon class="w-4 h-4 mr-1">edit</mat-icon>
                {{ 'COMMON.EDIT' | translate }}
              </button>
            </div>
          </div>

          <div class="mt-4 grid grid-cols-1 md:grid-cols-2 gap-4">
            <!-- Anmerkungen -->
            <div>
              <label class="block text-xs font-medium text-gray-700 mb-1">{{ 'APPOINTMENT_EDIT.FIELD_NOTES' | translate }}</label>
              <div
                *ngIf="!isFreigabeEditMode"
                class="w-full px-2 py-1.5 text-sm border border-gray-300 rounded bg-gray-50 text-gray-700 whitespace-pre-line break-words min-h-[2.5rem]">{{ kontrolltermin.anmerkungen || '-' }}</div>
              <div class="flex gap-2 mb-1"
                   *ngIf="isFreigabeEditMode && textTemplates.length">
                <select [(ngModel)]="selectedTemplateId" [ngModelOptions]="{standalone: true}"
                        class="flex-1 px-2 py-1 text-sm border border-gray-300 rounded focus:ring-1 focus:ring-blue-500 focus:border-blue-500">
                  <option [ngValue]="null">{{ 'APPOINTMENT_EDIT.SELECT_TEXT_TEMPLATE' | translate }}</option>
                  <option *ngFor="let t of textTemplates" [ngValue]="t.id">{{ t.schluesselwort }}</option>
                </select>
                <button type="button" (click)="insertTemplateIntoAnmerkungen()" [disabled]="!selectedTemplateId"
                        class="px-3 py-1 text-sm bg-blue-600 hover:bg-blue-700 text-white rounded disabled:opacity-50">
                  {{ 'APPOINTMENT_EDIT.INSERT_TEXT_TEMPLATE' | translate }}
                </button>
              </div>
              <textarea
                *ngIf="isFreigabeEditMode"
                formControlName="anmerkungen"
                rows="4"
                [placeholder]="'APPOINTMENT_EDIT.PLACEHOLDER_NOTES' | translate"
                class="w-full px-2 py-1.5 text-sm border border-gray-300 rounded focus:ring-1 focus:ring-blue-500 focus:border-blue-500 resize-y"></textarea>
            </div>

            <!-- Zusatz-Maßnahme -->
            <div>
              <label class="block text-xs font-medium text-gray-700 mb-1">{{ 'APPOINTMENT_EDIT.FIELD_ADDITIONAL_MEASURE' | translate }}</label>
              <div
                *ngIf="!isFreigabeEditMode"
                class="w-full px-2 py-1.5 text-sm border border-gray-300 rounded bg-gray-50 text-gray-700 whitespace-pre-line break-words min-h-[2.5rem]">{{ kontrolltermin.zusatz_massnahme || '-' }}</div>
              <textarea
                *ngIf="isFreigabeEditMode"
                formControlName="zusatz_massnahme"
                rows="4"
                [placeholder]="'APPOINTMENT_EDIT.PLACEHOLDER_ADDITIONAL_MEASURE' | translate"
                class="w-full px-2 py-1.5 text-sm border border-gray-300 rounded focus:ring-1 focus:ring-blue-500 focus:border-blue-500 resize-y"></textarea>
            </div>
          </div>

          <!-- Freigabe Edit Mode Buttons -->
          <div *ngIf="isFreigabeEditMode" class="mt-4 flex justify-end gap-2">
            <button
              (click)="cancelFreigabeEdit()"
              mat-stroked-button
              [disabled]="isSaving">
              {{ 'COMMON.CANCEL' | translate }}
            </button>
            <button
              (click)="saveFreigabeEdits()"
              mat-raised-button
              color="primary"
              [disabled]="isSaving || !kontrollterminForm.valid">
              <mat-icon class="w-4 h-4 mr-1">save</mat-icon>
              {{ 'COMMON.SAVE' | translate }}
            </button>
          </div>

          <!-- Eingesetzte Produkte Button -->
          <div class="mt-4">
            <button 
              mat-raised-button 
              color="primary"
              (click)="openEingesetzteProdukteDialog()"
              class="w-full">
              <mat-icon class="w-4 h-4 mr-1">inventory_2</mat-icon>
              {{ 'APPOINTMENT_EDIT.USED_PRODUCTS' | translate }}
            </button>
          </div>
        </div>
      </div>

      <!-- Section D: Status-Specific Footer Areas -->
      
      <!-- Bearbeitung Footer: Save and Complete Buttons -->
      <div *ngIf="sourceStatus === 'bearbeitung'" class="mb-4 p-4 bg-blue-50 border border-blue-200 rounded-lg">
        <div class="flex items-center justify-between gap-x-6">
          <div class="min-w-0 flex-1">
            <div class="flex items-start gap-x-3">
              <p class="text-sm font-semibold text-blue-900">{{ 'APPOINTMENT_EDIT.SECTION_COMPLETE_TITLE' | translate }}</p>
            </div>
            <p class="mt-1 text-xs text-blue-700">{{ 'APPOINTMENT_EDIT.SECTION_COMPLETE_HINT' | translate }}</p>
          </div>
        </div>
        
        <div class="mt-4 flex justify-end space-x-4">
          <button mat-button (click)="goBack()">{{ 'COMMON.CANCEL' | translate }}</button>
          <button mat-raised-button color="primary" (click)="saveKontrolltermin()" [disabled]="kontrollterminForm?.invalid">{{ 'COMMON.SAVE' | translate }}</button>
          <button mat-raised-button color="accent" (click)="abschliessen()">{{ 'APPOINTMENT_EDIT.COMPLETE' | translate }}</button>
        </div>
      </div>

      <!-- Freigabe Footer: Freigabe View -->
      <div *ngIf="sourceStatus === 'freigabe'" class="mb-4 p-4 bg-purple-50 border border-purple-200 rounded-lg">
        <div class="flex items-center justify-between gap-x-6">
          <div class="min-w-0 flex-1">
            <div class="flex items-start gap-x-3">
              <p class="text-sm font-semibold text-purple-900">{{ 'APPOINTMENT_EDIT.SECTION_FREIGABE_TITLE' | translate }}</p>
            </div>
            <p class="mt-1 text-xs text-purple-700">{{ 'APPOINTMENT_EDIT.SECTION_FREIGABE_HINT' | translate }}</p>
          </div>
        </div>
        
        <!-- Freigabe Form (always show on freigabe view as fallback) -->
        <div *ngIf="currentStatus === 'freigabe'" class="mt-4" [formGroup]="kontrollterminForm">
          <div class="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div>
              <label class="block text-xs font-medium text-gray-700 mb-1">{{ 'APPOINTMENT_EDIT.APPROVED_BY' | translate }}</label>
              <div class="text-sm text-gray-900 bg-gray-50 p-2 rounded border">
                {{ getCurrentUserName() }}
              </div>
            </div>
            
            <div>
              <label class="block text-xs font-medium text-gray-700 mb-1">{{ 'COMMON.STATUS' | translate }}</label>
              <div class="text-sm text-gray-900">
                <span class="inline-flex items-center px-2 py-1 rounded-full text-xs font-medium bg-yellow-100 text-yellow-800">
                  <mat-icon class="w-3 h-3 mr-1">pending</mat-icon>
                  {{ 'APPOINTMENT_EDIT.STATUS_PENDING' | translate }}
                </span>
              </div>
            </div>
          </div>
          
          <div class="mt-4" *ngIf="freigabeFormGroup" [formGroup]="freigabeFormGroup">
            <label class="block text-xs font-medium text-gray-700 mb-1">{{ 'APPOINTMENT_EDIT.FREIGABE_NOTES' | translate }}</label>
            <textarea
              formControlName="anmerkungen_freigabe"
              [placeholder]="'APPOINTMENT_EDIT.PLACEHOLDER_FREIGABE_NOTES' | translate"
              rows="3"
              class="w-full px-2 py-1.5 text-sm border border-gray-300 rounded focus:ring-2 focus:ring-purple-500 focus:border-purple-500 resize-none"></textarea>
          </div>
          
          <div class="mt-4 flex justify-end">
            <button
              (click)="confirmFreigabe()"
              mat-raised-button
              color="primary"
              class="bg-purple-600 hover:bg-purple-700 text-white">
              <mat-icon class="w-4 h-4 mr-2">check_circle</mat-icon>
              {{ 'APPOINTMENT_EDIT.CONFIRM_FREIGABE' | translate }}
            </button>
          </div>
        </div>
        
        <!-- Freigabe Details (only when status is abgeschlossen) -->
        <div *ngIf="currentStatus === 'abgeschlossen'" class="mt-4">
          <div class="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div>
              <label class="block text-xs font-medium text-gray-700 mb-1">{{ 'APPOINTMENT_EDIT.APPROVED_STATUS' | translate }}</label>
              <div class="text-sm text-gray-900">
                <span class="inline-flex items-center px-2 py-1 rounded-full text-xs font-medium bg-green-100 text-green-800">
                  <mat-icon class="w-3 h-3 mr-1">check_circle</mat-icon>
                  Ja
                </span>
              </div>
            </div>
            
            <div>
              <label class="block text-xs font-medium text-gray-700 mb-1">{{ 'APPOINTMENT_EDIT.APPROVAL_DATE' | translate }}</label>
              <div class="text-sm text-gray-900">{{ kontrolltermin.freigabe?.freigabe_datum | date:'dd.MM.yyyy HH:mm' || '-' }}</div>
            </div>
            
            <div>
              <label class="block text-xs font-medium text-gray-700 mb-1">{{ 'APPOINTMENT_EDIT.APPROVED_BY' | translate }}</label>
              <div class="text-sm text-gray-900">{{ getFreigabeDurchName() }}</div>
            </div>
            
            <div>
              <label class="block text-xs font-medium text-gray-700 mb-1">{{ 'APPOINTMENT_EDIT.SEND_DATE' | translate }}</label>
              <div class="text-sm text-gray-900">{{ kontrolltermin.freigabe?.versand_datum | date:'dd.MM.yyyy HH:mm' || '-' }}</div>
            </div>
          </div>
          
          <div class="mt-4">
            <label class="block text-xs font-medium text-gray-700 mb-1">{{ 'APPOINTMENT_EDIT.FREIGABE_NOTES' | translate }}</label>
            <div class="text-sm text-gray-900 bg-gray-50 p-3 rounded border">{{ kontrolltermin.freigabe?.anmerkungen_freigabe || '-' }}</div>
          </div>
          
          <!-- Button to navigate to report sending (only in freigabe view when status becomes abgeschlossen) -->
          <div *ngIf="sourceStatus === 'freigabe' && currentStatus === 'abgeschlossen'" class="mt-4 flex justify-end">
            <button
              (click)="navigateToAbgeschlossen()"
              mat-raised-button
              color="primary"
              class="bg-green-600 hover:bg-green-700 text-white">
              <mat-icon class="w-4 h-4 mr-2">arrow_forward</mat-icon>
              {{ 'APPOINTMENT_EDIT.TO_REPORT_SEND' | translate }}
            </button>
          </div>
        </div>

      </div>

      <!-- Freigabe Footer: Abgeschlossen View -->
      <div *ngIf="sourceStatus === 'abgeschlossen' && currentStatus === 'abgeschlossen'" class="mb-4 p-4 bg-purple-50 border border-purple-200 rounded-lg">
        <div class="flex items-center justify-between gap-x-6">
          <div class="min-w-0 flex-1">
            <div class="flex items-start gap-x-3">
              <p class="text-sm font-semibold text-purple-900">{{ 'APPOINTMENT_EDIT.SECTION_FREIGABE_TITLE' | translate }}</p>
            </div>
            <p class="mt-1 text-xs text-purple-700">{{ 'APPOINTMENT_EDIT.SECTION_FREIGABE_HINT' | translate }}</p>
          </div>
        </div>
        
        <!-- Freigabe Details -->
        <div class="mt-4">
          <div class="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div>
              <label class="block text-xs font-medium text-gray-700 mb-1">{{ 'APPOINTMENT_EDIT.APPROVED_STATUS' | translate }}</label>
              <div class="text-sm text-gray-900">
                <span class="inline-flex items-center px-2 py-1 rounded-full text-xs font-medium bg-green-100 text-green-800">
                  <mat-icon class="w-3 h-3 mr-1">check_circle</mat-icon>
                  {{ 'COMMON.YES' | translate }}
                </span>
              </div>
            </div>
            
            <div>
              <label class="block text-xs font-medium text-gray-700 mb-1">{{ 'APPOINTMENT_EDIT.APPROVAL_DATE' | translate }}</label>
              <div class="text-sm text-gray-900">{{ kontrolltermin.freigabe?.freigabe_datum | date:'dd.MM.yyyy HH:mm' || '-' }}</div>
            </div>
            
            <div>
              <label class="block text-xs font-medium text-gray-700 mb-1">{{ 'APPOINTMENT_EDIT.APPROVED_BY' | translate }}</label>
              <div class="text-sm text-gray-900">{{ getFreigabeDurchName() }}</div>
            </div>
          </div>
          
          <div class="mt-4">
            <label class="block text-xs font-medium text-gray-700 mb-1">{{ 'APPOINTMENT_EDIT.FREIGABE_NOTES' | translate }}</label>
            <div class="text-sm text-gray-900 bg-gray-50 p-3 rounded border">{{ kontrolltermin.freigabe?.anmerkungen_freigabe || '-' }}</div>
          </div>
        </div>
      </div>

      
      <!-- Bericht Area (appears only when status is abgeschlossen) -->
      <div *ngIf="currentStatus === 'abgeschlossen' && sourceStatus !== 'freigabe'" class="mb-4 space-y-4">
        <!-- Step 1: Generate Report (only when freigabe is done - abgeschlossen) -->
        <div *ngIf="currentStatus === 'abgeschlossen'" class="p-4 bg-green-50 border border-green-200 rounded-lg">
          <div class="flex items-center justify-between gap-x-6 mb-4">
            <div class="min-w-0 flex-1">
              <div class="flex items-center gap-x-3">
                <span class="inline-flex items-center justify-center w-8 h-8 rounded-full bg-green-600 text-white text-sm font-semibold">1</span>
                <p class="text-sm font-semibold text-green-900">{{ 'APPOINTMENT_EDIT.GENERATE_REPORT' | translate }}</p>
              </div>
              <p class="mt-1 text-xs text-green-700 ml-11">{{ 'APPOINTMENT_EDIT.GENERATE_REPORT' | translate }}</p>
            </div>
          </div>
          
          <div class="ml-11 space-y-4">
            <!-- Report Category Selection and Generate Button -->
            <div class="flex items-center gap-4">
              <!-- Category Dropdown (Custom Style) -->
              <div class="flex-none category-dropdown-container">
                <div class="relative">
                  <button 
                    type="button"
                    class="inline-flex w-full justify-center gap-x-1.5 rounded-md bg-white px-3 py-2 text-sm font-semibold text-gray-900 shadow-sm ring-1 ring-inset ring-gray-300 hover:bg-gray-50 dark:bg-white/10 dark:text-white dark:shadow-none dark:ring-white/5 dark:hover:bg-white/20"
                    (click)="toggleCategoryDropdown($event)"
                    [attr.aria-expanded]="isCategoryDropdownOpen"
                    aria-haspopup="listbox">
                    <span *ngIf="getSelectedCategoryOption()" class="flex items-center gap-2">
                      <mat-icon class="w-4 h-4">{{ getSelectedCategoryOption()?.icon }}</mat-icon>
                      <span>{{ getSelectedCategoryOption()?.label }}</span>
                    </span>
                    <span *ngIf="!getSelectedCategoryOption()">{{ 'REPORTS.SELECT_CATEGORY' | translate }}</span>
                    <svg viewBox="0 0 20 20" fill="currentColor" aria-hidden="true" class="-mr-1 h-5 w-5 text-gray-400">
                      <path d="M5.22 8.22a.75.75 0 0 1 1.06 0L10 11.94l3.72-3.72a.75.75 0 1 1 1.06 1.06l-4.25 4.25a.75.75 0 0 1-1.06 0L5.22 9.28a.75.75 0 0 1 0-1.06Z" clip-rule="evenodd" fill-rule="evenodd" />
                    </svg>
                  </button>

                  <div 
                    *ngIf="isCategoryDropdownOpen"
                    class="absolute right-0 z-10 mt-2 w-64 origin-top-right rounded-md bg-white shadow-lg ring-1 ring-black ring-opacity-5 focus:outline-none dark:bg-gray-800 dark:ring-white/10"
                    role="menu"
                    aria-orientation="vertical"
                    aria-labelledby="category-menu-button"
                    tabindex="-1">
                    <div class="py-1" role="none">
                      <!-- Group 1: Besucherberichte -->
                      <button
                        *ngFor="let category of getBesucherberichtCategories()"
                        type="button"
                        class="block w-full px-4 py-2 text-left text-sm text-gray-700 hover:bg-gray-100 hover:text-gray-900 focus:bg-gray-100 focus:text-gray-900 focus:outline-none dark:text-gray-300 dark:hover:bg-white/5 dark:hover:text-white dark:focus:bg-white/5 dark:focus:text-white disabled:opacity-50 disabled:cursor-not-allowed"
                        role="menuitem"
                        tabindex="-1"
                        [disabled]="!category.available"
                        (click)="selectCategory(category.value, $event)"
                        [class.bg-gray-100]="selectedReportCategory === category.value"
                        [class.text-gray-900]="selectedReportCategory === category.value">
                        <div class="flex items-center gap-2">
                          <mat-icon class="w-4 h-4">{{ category.icon }}</mat-icon>
                          <span>{{ category.label }}</span>
                          <mat-icon *ngIf="!category.available" class="w-4 h-4 text-orange-500 ml-auto">warning</mat-icon>
                        </div>
                      </button>
                      
                      <!-- Separator 1 -->
                      <div class="border-t border-gray-200 my-1 dark:border-gray-700"></div>
                      
                      <!-- Group 2: Struktur/Pläne -->
                      <button
                        *ngFor="let category of getStrukturCategories()"
                        type="button"
                        class="block w-full px-4 py-2 text-left text-sm text-gray-700 hover:bg-gray-100 hover:text-gray-900 focus:bg-gray-100 focus:text-gray-900 focus:outline-none dark:text-gray-300 dark:hover:bg-white/5 dark:hover:text-white dark:focus:bg-white/5 dark:focus:text-white disabled:opacity-50 disabled:cursor-not-allowed"
                        role="menuitem"
                        tabindex="-1"
                        [disabled]="!category.available"
                        (click)="selectCategory(category.value, $event)"
                        [class.bg-gray-100]="selectedReportCategory === category.value"
                        [class.text-gray-900]="selectedReportCategory === category.value">
                        <div class="flex items-center gap-2">
                          <mat-icon class="w-4 h-4">{{ category.icon }}</mat-icon>
                          <span>{{ category.label }}</span>
                          <mat-icon *ngIf="!category.available" class="w-4 h-4 text-orange-500 ml-auto">warning</mat-icon>
                        </div>
                      </button>
                      
                      <!-- Separator 2 -->
                      <div class="border-t border-gray-200 my-1 dark:border-gray-700"></div>
                      
                      <!-- Group 3: Trend-Analyse -->
                      <button
                        *ngFor="let category of getTrendCategories()"
                        type="button"
                        class="block w-full px-4 py-2 text-left text-sm text-gray-700 hover:bg-gray-100 hover:text-gray-900 focus:bg-gray-100 focus:text-gray-900 focus:outline-none dark:text-gray-300 dark:hover:bg-white/5 dark:hover:text-white dark:focus:bg-white/5 dark:focus:text-white disabled:opacity-50 disabled:cursor-not-allowed"
                        role="menuitem"
                        tabindex="-1"
                        [disabled]="!category.available"
                        (click)="selectCategory(category.value, $event)"
                        [class.bg-gray-100]="selectedReportCategory === category.value"
                        [class.text-gray-900]="selectedReportCategory === category.value">
                        <div class="flex items-center gap-2">
                          <mat-icon class="w-4 h-4">{{ category.icon }}</mat-icon>
                          <span>{{ category.label }}</span>
                          <mat-icon *ngIf="!category.available" class="w-4 h-4 text-orange-500 ml-auto">warning</mat-icon>
                        </div>
                      </button>
                    </div>
                  </div>
                </div>
              </div>
              
              <!-- Generate Button -->
              <button
                (click)="generateReport()"
                [disabled]="isGeneratingReport"
                mat-raised-button
                color="primary"
                class="bg-green-600 hover:bg-green-700 text-white">
                <mat-icon class="w-4 h-4 mr-2">description</mat-icon>
                <span *ngIf="isGeneratingReport">{{ 'REPORTS.GENERATING' | translate }}</span>
                <span *ngIf="!isGeneratingReport">{{ 'APPOINTMENT_EDIT.GENERATE_REPORT' | translate }}</span>
              </button>
            </div>

            <!-- Trend Analysis Options (shown when trend_analyse is selected) -->
            <div *ngIf="selectedReportCategory === 'trend_analyse'" class="mt-3 p-4 bg-blue-50 dark:bg-white/5 rounded-lg border border-blue-200 dark:border-white/10">
              <div class="flex flex-wrap items-end gap-4">
                <!-- Report Sub-Type -->
                <div class="flex flex-col">
                  <label class="text-xs font-medium text-gray-600 dark:text-gray-400 mb-1">{{ 'APPOINTMENT_EDIT.REPORT_TYPE' | translate }}</label>
                  <select 
                    [(ngModel)]="trendReportType"
                    [ngModelOptions]="{standalone: true}"
                    class="rounded-md border border-gray-300 bg-white px-3 py-2 text-sm text-gray-900 shadow-sm focus:border-blue-500 focus:ring-1 focus:ring-blue-500 dark:bg-gray-700 dark:text-white dark:border-gray-600"
                    style="color: #111; background-color: #fff;">
                    <option *ngFor="let type of trendReportTypes" [value]="type.value" style="color: #111; background-color: #fff;">{{ type.label }}</option>
                  </select>
                </div>

                <!-- Year Selection -->
                <div class="flex flex-col">
                  <label class="text-xs font-medium text-gray-600 dark:text-gray-400 mb-1">{{ 'APPOINTMENT_EDIT.YEAR' | translate }}</label>
                  <select
                    [(ngModel)]="trendSelectedYear"
                    [ngModelOptions]="{standalone: true}"
                    (ngModelChange)="onTrendYearChange()"
                    class="rounded-md border border-gray-300 bg-white px-3 py-2 text-sm text-gray-900 shadow-sm focus:border-blue-500 focus:ring-1 focus:ring-blue-500 dark:bg-gray-700 dark:text-white dark:border-gray-600"
                    style="color: #111; background-color: #fff;">
                    <option *ngFor="let year of trendYearOptions" [ngValue]="year" style="color: #111; background-color: #fff;">{{ year }}</option>
                  </select>
                </div>

                <!-- Date range display -->
                <div class="flex flex-col">
                  <label class="text-xs font-medium text-gray-600 dark:text-gray-400 mb-1">{{ 'APPOINTMENT_EDIT.TIME_PERIOD' | translate }}</label>
                  <span class="text-sm text-gray-700 dark:text-gray-300 py-2">01.01.{{ trendSelectedYear }} &ndash; 31.12.{{ trendSelectedYear }}</span>
                </div>
              </div>

              <!-- Info text -->
              <div class="mt-2 text-xs text-gray-500 dark:text-gray-400">
                {{ 'APPOINTMENT_EDIT.PREVIOUS_YEAR_HINT' | translate }} ({{ trendSelectedYear - 1 }})
              </div>
            </div>
            
            <!-- 2-column layout: Status Display (left) and Report Versions (right) -->
            <!-- Show grid if there are report versions OR if there's report info -->
            <div *ngIf="(reportVersions && reportVersions.length > 0) || reportInfo" class="grid grid-cols-1 lg:grid-cols-2 gap-4">
              <!-- Left Column: Report Status Display -->
              <!-- Only show this card if it's a standard or besuchsbericht report (these are the ones attached to emails) -->
              <!-- Also show if report_category is null/undefined (backward compatibility - treat as standard) -->
              <div *ngIf="reportInfo && (!reportInfo.report_category || reportInfo.report_category === 'standard' || reportInfo.report_category === 'besuchsbericht')" class="p-4 bg-gray-50 rounded-lg border border-green-200 text-sm">
                <div class="flex justify-between items-center mb-3">
                  <div>
                    <span class="font-medium">{{ 'COMMON.STATUS' | translate }}:</span>
                    <span class="ml-2 px-2 py-1 rounded text-xs bg-green-100 text-green-800">
                      {{ 'APPOINTMENT_EDIT.GENERATED' | translate }}
                    </span>
                  </div>
                  <div class="text-gray-600">
                    {{ getFormattedFileSize() }}
                  </div>
                </div>
                
                <div class="bg-white p-3 rounded border">
                  <div class="flex items-center mb-2">
                    <mat-icon class="w-4 h-4 mr-2 text-green-600">description</mat-icon>
                    <span class="font-medium text-gray-700">{{ 'APPOINTMENT_EDIT.GENERATED' | translate }}</span>
                    <span *ngIf="reportInfo?.version" class="ml-2 px-2 py-1 bg-green-100 text-green-800 text-xs rounded">
                      Version {{ reportInfo.version }}
                    </span>
                  </div>
                  <div class="text-sm text-gray-600">
                    <div><strong>{{ 'COMMON.DATE' | translate }}:</strong> {{ getFormattedGenerationDate() }}</div>
                    <div><strong>{{ 'COMMON.BY' | translate }}:</strong> {{ getGeneratedByName() }}</div>
                    <div *ngIf="reportInfo?.version"><strong>{{ 'COMMON.VERSION' | translate }}:</strong> {{ reportInfo.version }}</div>
                  </div>
                  
                  <!-- Notification Text -->
                  <div class="mt-3 p-2 bg-blue-50 border border-blue-200 rounded text-xs text-blue-800">
                    {{ 'APPOINTMENT_EDIT.REPORT_CLIENT_NOTIFICATION' | translate }}
                  </div>
                  
                  <!-- Report Action Buttons -->
                  <div class="mt-3 flex flex-wrap gap-2">
                    <button
                      (click)="viewReport()"
                      [disabled]="!hasReportGenerated"
                      mat-button
                      class="text-blue-600 hover:text-blue-800 text-sm">
                      <mat-icon class="w-4 h-4 mr-1">description</mat-icon>
                      {{ 'APPOINTMENT_EDIT.VIEW_REPORT' | translate }}
                    </button>
                    
                    <button
                      (click)="viewReportHtml()"
                      [disabled]="!hasReportGenerated"
                      mat-button
                      class="text-green-600 hover:text-green-800 text-sm">
                      <mat-icon class="w-4 h-4 mr-1">code</mat-icon>
                      {{ 'APPOINTMENT_EDIT.PREVIEW' | translate }}
                    </button>
                  </div>
                </div>
              </div>

              <!-- Right Column: Report Versions List -->
              <div class="p-4 bg-gray-50 border border-gray-200 rounded-lg">
                <div class="flex items-center justify-between gap-x-6 mb-4">
                  <div class="min-w-0 flex-1">
                    <div class="flex items-start gap-x-3">
                      <p class="text-sm font-semibold text-gray-900 dark:text-white">{{ 'APPOINTMENT_EDIT.REPORT_VERSIONS' | translate }}</p>
                    </div>
                    <p class="mt-1 text-xs text-gray-600">{{ 'APPOINTMENT_EDIT.REPORT_VERSIONS_HINT' | translate }}</p>
                  </div>
                </div>

                <!-- Report Versions List with scroll (shows 2.5 cards) -->
                <div *ngIf="reportVersions && reportVersions.length > 0" class="space-y-2 max-h-[350px] overflow-y-auto pr-2">
                  <div *ngFor="let report of reportVersions" 
                       class="flex items-center justify-between p-3 bg-white border border-gray-200 rounded-lg hover:bg-gray-50 transition-colors">
                    <div class="flex items-center gap-3 flex-1 min-w-0">
                      <!-- PDF Icon (clickable) -->
                      <button
                        (click)="downloadReportVersion(report.uuid)"
                        mat-icon-button
                        class="text-red-600 hover:text-red-800 flex-shrink-0"
                        [title]="'APPOINTMENT_EDIT.DOWNLOAD_REPORT' | translate">
                        <mat-icon class="w-5 h-5">picture_as_pdf</mat-icon>
                      </button>
                      
                      <!-- Report Details -->
                      <div class="flex-1 min-w-0">
                        <!-- Category as main heading -->
                        <div class="flex items-center gap-2 mb-1">
                          <span class="text-sm font-medium text-gray-900">
                            {{ getCategoryLabel(report.report_category || 'standard') }}
                          </span>
                          <span *ngIf="report.uuid === reportInfo?.uuid && (report.report_category || 'standard') === 'standard'" 
                                class="px-2 py-0.5 bg-green-100 text-green-800 text-xs rounded">
                            {{ 'APPOINTMENT_EDIT.REPORT_CURRENT' | translate }}
                          </span>
                        </div>
                        <!-- Version and date as smaller info -->
                        <div class="flex items-center gap-2 text-xs text-gray-500">
                          <span>{{ 'COMMON.VERSION' | translate }} {{ report.version }}</span>
                          <span>•</span>
                          <span>{{ formatReportDate(report.created_at) }}</span>
                        </div>
                        <!-- Category badge in 3rd line -->
                        <div class="mt-1">
                          <span [ngClass]="getCategoryBadgeClass(report.report_category || 'standard')" 
                                class="inline-flex items-center rounded-md px-2 py-1 text-xs font-medium ring-1 ring-inset">
                            {{ getCategoryLabel(report.report_category || 'standard') }}
                          </span>
                        </div>
                      </div>
                    </div>
                    
                    <!-- Action Buttons -->
                    <div class="flex flex-col gap-2 flex-shrink-0">
                      <button
                        (click)="viewReportHtml(report.uuid)"
                        mat-button
                        class="text-blue-600 hover:text-blue-800 text-sm border border-gray-300 hover:border-blue-400">
                        {{ 'APPOINTMENT_EDIT.PREVIEW' | translate }}
                      </button>
                      
                      <button
                        (click)="downloadReportVersion(report.uuid)"
                        mat-button
                        class="text-red-600 hover:text-red-800 text-sm border border-gray-300 hover:border-red-400">
                        {{ 'APPOINTMENT_EDIT.PDF_EXPORT' | translate }}
                      </button>
                    </div>
                  </div>
                </div>

                <!-- Empty State -->
                <div *ngIf="!reportVersions || reportVersions.length === 0" 
                     class="text-center py-6 text-sm text-gray-500">
                  <mat-icon class="w-8 h-8 mb-2 text-gray-400">description</mat-icon>
                  <p>{{ 'APPOINTMENT_EDIT.NO_REPORTS' | translate }}</p>
                </div>
              </div>
            </div>
          </div>
        </div>

        <!-- Step 2: Send Report by Mail (only when there is a report to send) -->
        <div *ngIf="hasReportGenerated" class="p-4 bg-blue-50 border border-blue-200 rounded-lg">
          <div class="flex items-center justify-between gap-x-6 mb-4">
            <div class="min-w-0 flex-1">
              <div class="flex items-center gap-x-3">
                <span class="inline-flex items-center justify-center w-8 h-8 rounded-full bg-blue-600 text-white text-sm font-semibold">2</span>
                <p class="text-sm font-semibold text-blue-900">{{ 'APPOINTMENT_EDIT.SEND_REPORT_EMAIL' | translate }}</p>
              </div>
              <p class="mt-1 text-xs text-blue-700 ml-11">{{ 'APPOINTMENT_EDIT.SELECT_RECIPIENTS_HINT' | translate }}</p>
            </div>
          </div>
          
          <div class="ml-11 space-y-4">
            <!-- 2-column layout: Contacts + Editor (left 2/3) and Send Status (right 1/3) -->
            <div class="grid grid-cols-1 lg:grid-cols-3 gap-4">
              <!-- Left Column: Email Recipients Section + Editor (2/3 width) -->
              <div class="email-recipients-section lg:col-span-2">
                <label class="block text-xs font-medium text-gray-700 mb-2">{{ 'APPOINTMENT_EDIT.EMAIL_RECIPIENTS' | translate }}</label>
                
                <!-- Saved Contacts List -->
                <div class="saved-contacts mb-4" *ngIf="savedContacts.length > 0">
                  <div class="text-xs text-gray-600 mb-2">{{ 'APPOINTMENT_EDIT.SAVED_CONTACTS' | translate }}</div>
                  <div *ngFor="let contact of savedContacts; let i = index" 
                       class="contact-checkbox-item flex items-center p-2 border rounded mb-2 bg-white hover:bg-gray-50 transition-colors">
                    <input type="checkbox" 
                           [(ngModel)]="contact.selected" 
                           [ngModelOptions]="{standalone: true}"
                           class="mr-2 transform scale-110" />
                    <div class="flex-1">
                      <div class="text-xs font-medium text-gray-900">{{ contact.name || ('COMMON.UNKNOWN' | translate) }}</div>
                      <div class="text-xs text-gray-600">{{ contact.email }}</div>
                      <div class="text-xs text-gray-500">{{ contact.anrede || ('COMMON.NO_GREETING' | translate) }}</div>
                    </div>
                  </div>
                </div>
                
                <!-- No saved contacts message -->
                <div *ngIf="savedContacts.length === 0" class="text-xs text-gray-500 mb-4 p-2 bg-yellow-50 border border-yellow-200 rounded">
                  {{ 'APPOINTMENT_EDIT.NO_CONTACTS' | translate }}
                </div>
                
                <!-- Additional Contact Input - Accordion -->
                <div class="additional-contact mb-4">
                  <button
                    type="button"
                    (click)="toggleAdditionalContactAccordion()"
                    class="w-full flex items-center justify-between p-2 text-xs font-medium text-gray-700 bg-white border border-blue-200 rounded-lg hover:bg-gray-50 transition-colors">
                    <span>{{ 'APPOINTMENT_EDIT.ADDITIONAL_RECIPIENT' | translate }}</span>
                    <mat-icon class="w-4 h-4 transform transition-transform" [class.rotate-180]="isAdditionalContactExpanded">
                      expand_more
                    </mat-icon>
                  </button>
                  
                  <div *ngIf="isAdditionalContactExpanded" class="mt-2 p-3 bg-white border border-blue-200 rounded-lg">
                    <div class="flex flex-col sm:flex-row gap-2 mb-2">
                      <input type="email" 
                             [(ngModel)]="additionalContact.email" 
                             [ngModelOptions]="{standalone: true}"
                             [placeholder]="'APPOINTMENT_EDIT.PLACEHOLDER_EMAIL' | translate"
                             class="flex-1 px-2 py-1 text-xs border border-gray-300 focus:ring-1 focus:ring-blue-500 focus:border-blue-500"
                             style="border-radius: 4px;" />
                      <input type="text" 
                             [(ngModel)]="additionalContact.name" 
                             [ngModelOptions]="{standalone: true}"
                             placeholder="Name (optional)"
                             class="flex-1 px-2 py-1 text-xs border border-gray-300 focus:ring-1 focus:ring-blue-500 focus:border-blue-500"
                             style="border-radius: 4px;" />
                    </div>
                    <input type="text" 
                           [(ngModel)]="additionalContact.anrede" 
                           [ngModelOptions]="{standalone: true}"
                           placeholder="Anrede (optional)"
                           class="w-full px-2 py-1 text-xs border border-gray-300 focus:ring-1 focus:ring-blue-500 focus:border-blue-500"
                           style="border-radius: 4px;" />
                  </div>
                </div>

                <!-- Email Subject - in left column -->
                <div class="mt-4">
                  <label class="block text-xs font-medium text-gray-700 mb-2">{{ 'APPOINTMENT_EDIT.SUBJECT' | translate }}</label>
                  <input 
                    type="text" 
                    [(ngModel)]="emailSubject"
                    [ngModelOptions]="{standalone: true}"
                    [placeholder]="'APPOINTMENT_EDIT.SUBJECT' | translate"
                    class="w-full px-3 py-2 text-sm border border-gray-300 rounded-md focus:ring-1 focus:ring-blue-500 focus:border-blue-500">
                </div>

                <!-- PDF Report Attachment Info - below subject -->
                <div *ngIf="reportInfo && reportInfo.filename" class="mt-3 p-3 bg-blue-50 border border-blue-200 rounded-md">
                  <div class="flex items-center gap-2">
                    <mat-icon class="text-red-600" style="font-size: 20px; width: 20px; height: 20px;">picture_as_pdf</mat-icon>
                    <div class="flex-1 min-w-0">
                      <div class="text-xs font-medium text-gray-700 mb-1">{{ 'APPOINTMENT_EDIT.ATTACHED_DOCUMENT' | translate }}</div>
                      <button
                        type="button"
                        (click)="openReportInNewTab()"
                        class="text-xs text-blue-600 hover:text-blue-800 hover:underline cursor-pointer flex items-center gap-1 focus:outline-none focus:ring-2 focus:ring-blue-500 rounded px-1 -ml-1"
                        [title]="reportInfo.filename">
                        <span>{{ getTruncatedFilename(reportInfo.filename) }}</span>
                        <mat-icon class="text-xs" style="font-size: 14px; width: 14px; height: 14px;">open_in_new</mat-icon>
                      </button>
                    </div>
                  </div>
                </div>

                <!-- Email Editor (TinyMCE) - in left column -->
                <div class="mt-4">
                  <label class="block text-xs font-medium text-gray-700 mb-2">{{ 'APPOINTMENT_EDIT.EMAIL_MESSAGE' | translate }}</label>
                  <editor
                    [(ngModel)]="emailMessage"
                    [ngModelOptions]="{standalone: true}"
                    [init]="tinymceInit">
                  </editor>
                </div>

                <!-- Send Buttons (below editor in left column) -->
                <div class="flex justify-end space-x-3 mt-4">
                  <button
                    (click)="sendReportToSelected()"
                    [disabled]="!hasReportGenerated || isSendingReport || getSelectedEmailAddresses().length === 0"
                    mat-raised-button
                    color="primary"
                    class="bg-blue-600 hover:bg-blue-700 text-white">
                    <span *ngIf="isSendingReport">{{ 'APPOINTMENT_EDIT.SENDING' | translate }}</span>
                    <span *ngIf="!isSendingReport">
                      {{ hasReportSent ? ('APPOINTMENT_EDIT.RESEND' | translate) : ('APPOINTMENT_EDIT.SEND_REPORT' | translate) }}
                    </span>
                  </button>
                </div>
              </div>

              <!-- Right Column: Send Status Display (1/3 width) -->
              <div>
                <div *ngIf="hasReportSent && reportInfo" class="p-4 bg-gray-50 border border-gray-200 rounded-lg">
                  <div class="flex items-center justify-between gap-x-6 mb-4">
                    <div class="min-w-0 flex-1">
                      <div class="flex items-start gap-x-3">
                        <p class="text-sm font-semibold text-gray-900 dark:text-white">{{ 'APPOINTMENT_EDIT.SEND_INFO' | translate }}</p>
                      </div>
                    </div>
                  </div>
                  
                  <div class="bg-white p-3 rounded border">
                    <div class="flex items-center mb-2">
                      <mat-icon class="w-4 h-4 mr-2 text-green-600">send</mat-icon>
                      <span class="font-medium text-gray-700">{{ 'APPOINTMENT_EDIT.SENT' | translate }}</span>
                    </div>
                    <div class="text-sm text-gray-600">
                      <div><strong>{{ 'COMMON.DATE' | translate }}:</strong> {{ getFormattedSendDate() }}</div>
                      <div><strong>{{ 'COMMON.BY' | translate }}:</strong> {{ getSentByName() }}</div>
                      <div><strong>{{ 'COMMON.TO' | translate }}:</strong> {{ getSentToEmails() }}</div>
                    </div>
                  </div>
                </div>

                <!-- Empty state for right column when no report sent -->
                <div *ngIf="!hasReportSent || !reportInfo" class="p-4 bg-gray-50 border border-gray-200 rounded-lg">
                  <div class="flex items-center justify-between gap-x-6 mb-4">
                    <div class="min-w-0 flex-1">
                      <div class="flex items-start gap-x-3">
                        <p class="text-sm font-semibold text-gray-900 dark:text-white">{{ 'APPOINTMENT_EDIT.SEND_INFO' | translate }}</p>
                      </div>
                    </div>
                  </div>
                  
                  <div class="text-center py-6 text-sm text-gray-500">
                    <mat-icon class="w-8 h-8 mb-2 text-gray-400">send</mat-icon>
                    <p>{{ 'APPOINTMENT_EDIT.NOT_SENT' | translate }}</p>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
        
      </div>

    </div>
    
    <!-- Legacy header for other statuses (aktuell, etc.) -->
    <div *ngIf="sourceStatus !== 'bearbeitung' && sourceStatus !== 'freigabe' && sourceStatus !== 'abgeschlossen' && !['geloescht', 'abgebrochen', 'fehlerhaft', 'geplant'].includes(sourceStatus)">
      <!-- Header Info Component -->
      <app-header-info 
        [kontrolltermin]="kontrolltermin" 
        [editableFields]="getEditableFields()"
        [showTimeFields]="true"
        [sourceStatus]="sourceStatus"
        (batchFieldsChanged)="onHeaderBatchChanged($event)">
      </app-header-info>

      <!-- Form Content -->
      <form [formGroup]="kontrollterminForm" class="mt-4 grid grid-cols-1 lg:grid-cols-2 gap-4">
        <!-- Left Column -->
        <div class="space-y-3">
          <!-- Einsatznummer -->
          <div>
            <label class="block text-xs font-medium text-gray-700 mb-1">{{ 'HEADER_INFO.EINSATZNUMMER' | translate }}</label>
            <input 
              type="text" 
              [value]="kontrolltermin.einsatznummer"
              disabled
              class="w-full px-2 py-1.5 text-sm border border-gray-300 rounded bg-gray-50 text-gray-500">
          </div>

          <!-- Standort -->
          <div>
            <label class="block text-xs font-medium text-gray-700 mb-1">{{ 'HEADER_INFO.LOCATION' | translate }} *</label>
            <input 
              *ngIf="!isEditMode"
              type="text" 
              [value]="kontrolltermin.standort?.name || ('COMMON.NOT_ASSIGNED' | translate)"
              disabled
              class="w-full px-2 py-1.5 text-sm border border-gray-300 rounded bg-gray-50 text-gray-500">
            <select
              *ngIf="isEditMode"
              formControlName="standort_id"
              class="w-full px-2 py-1.5 text-sm border border-gray-300 rounded focus:ring-1 focus:ring-blue-500 focus:border-blue-500">
              <option value="">{{ 'APPOINTMENT_EDIT.SELECT_STANDORT' | translate }}</option>
              <!-- TODO: Populate with actual standort options -->
            </select>
          </div>

          <!-- Projektaufgabe -->
          <div>
            <label class="block text-xs font-medium text-gray-700 mb-1">{{ 'APPOINTMENT_EDIT.FIELD_PROJEKTAUFGABE' | translate }} *</label>
            <input 
              *ngIf="!isEditMode"
              type="text" 
              [value]="kontrolltermin.projektaufgabe?.beschreibung || ('COMMON.NOT_ASSIGNED' | translate)"
              disabled
              class="w-full px-2 py-1.5 text-sm border border-gray-300 rounded bg-gray-50 text-gray-500">
            <select
              *ngIf="isEditMode"
              formControlName="projektaufgabe_id"
              class="w-full px-2 py-1.5 text-sm border border-gray-300 rounded focus:ring-1 focus:ring-blue-500 focus:border-blue-500">
              <option value="">{{ 'APPOINTMENT_EDIT.SELECT_PROJEKTAUFGABE' | translate }}</option>
              <!-- TODO: Populate with actual projektaufgabe options -->
            </select>
          </div>

          <!-- Kunde -->
          <div>
            <label class="block text-xs font-medium text-gray-700 mb-1">{{ 'HEADER_INFO.CLIENT' | translate }} *</label>
            <input 
              *ngIf="!isEditMode"
              type="text" 
              [value]="kontrolltermin.kunde?.suchbegriff || ('COMMON.NOT_ASSIGNED' | translate)"
              disabled
              class="w-full px-2 py-1.5 text-sm border border-gray-300 rounded bg-gray-50 text-gray-500">
            <select
              *ngIf="isEditMode"
              formControlName="kunde_id"
              class="w-full px-2 py-1.5 text-sm border border-gray-300 rounded focus:ring-1 focus:ring-blue-500 focus:border-blue-500">
              <option value="">{{ 'APPOINTMENT_EDIT.SELECT_KUNDE' | translate }}</option>
              <!-- TODO: Populate with actual kunde options -->
            </select>
          </div>

          <!-- Techniker -->
          <div>
            <label class="block text-xs font-medium text-gray-700 mb-1">{{ 'HEADER_INFO.TECHNICIAN' | translate }} *</label>
            <input 
              *ngIf="!isEditMode"
              type="text" 
              [value]="getTechnikerName(kontrolltermin.techniker) || ('COMMON.NOT_ASSIGNED' | translate)"
              disabled
              class="w-full px-2 py-1.5 text-sm border border-gray-300 rounded bg-gray-50 text-gray-500">
            <select
              *ngIf="isEditMode"
              formControlName="techniker_id"
              class="w-full px-2 py-1.5 text-sm border border-gray-300 rounded focus:ring-1 focus:ring-blue-500 focus:border-blue-500">
              <option value="">{{ 'APPOINTMENT_EDIT.SELECT_TECHNIKER' | translate }}</option>
              <!-- TODO: Populate with actual techniker options -->
            </select>
          </div>

          <!-- Einsatztyp -->
          <div>
            <label class="block text-xs font-medium text-gray-700 mb-1">{{ 'APPOINTMENT_EDIT.FIELD_EINSATZTYP' | translate }} *</label>
            <input 
              *ngIf="!isEditMode"
              type="text" 
              [value]="getEinsatztypText(kontrolltermin.einsatztyp)"
              disabled
              class="w-full px-2 py-1.5 text-sm border border-gray-300 rounded bg-gray-50 text-gray-500">
            <select
              *ngIf="isEditMode"
              formControlName="einsatztyp"
              class="w-full px-2 py-1.5 text-sm border border-gray-300 rounded focus:ring-1 focus:ring-blue-500 focus:border-blue-500">
              <option value="">{{ 'APPOINTMENT_EDIT.SELECT_EINSATZTYP' | translate }}</option>
              <option *ngFor="let option of einsatztypOptions" [value]="option.value">
                {{ option.label }}
              </option>
            </select>
          </div>
        </div>

        <!-- Right Column -->
        <div class="space-y-3">
          <!-- Status -->
          <div>
            <label class="block text-xs font-medium text-gray-700 mb-1">{{ 'COMMON.STATUS' | translate }} *</label>
            <input 
              *ngIf="!isEditMode"
              type="text" 
              [value]="getStatusText(kontrolltermin.status_id)"
              disabled
              class="w-full px-2 py-1.5 text-sm border border-gray-300 rounded bg-gray-50 text-gray-500">
            <select
              *ngIf="isEditMode"
              formControlName="status_id"
              class="w-full px-2 py-1.5 text-sm border border-gray-300 rounded focus:ring-1 focus:ring-blue-500 focus:border-blue-500">
              <option value="">{{ 'APPOINTMENT_EDIT.SELECT_STATUS' | translate }}</option>
              <option *ngFor="let option of statusOptions" [value]="option.id">
                {{ option.label }}
              </option>
            </select>
          </div>

          <!-- Datum Durchführung -->
          <div>
            <label class="block text-xs font-medium text-gray-700 mb-1">{{ 'APPOINTMENT_EDIT.FIELD_DATUM_DURCHFUEHRUNG' | translate }} *</label>
            <input 
              *ngIf="!isEditMode"
              type="text" 
              [value]="kontrolltermin.datum_durchfuerung | date:'dd.MM.yyyy'"
              disabled
              class="w-full px-2 py-1.5 text-sm border border-gray-300 rounded bg-gray-50 text-gray-500">
            <input
              *ngIf="isEditMode"
              type="date"
              formControlName="datum_durchfuerung"
              class="w-full px-2 py-1.5 text-sm border border-gray-300 rounded focus:ring-1 focus:ring-blue-500 focus:border-blue-500">
          </div>

          <!-- Monat Plan -->
          <div>
            <label class="block text-xs font-medium text-gray-700 mb-1">{{ 'APPOINTMENT_EDIT.FIELD_MONAT_PLAN' | translate }}</label>
            <input 
              *ngIf="!isEditMode"
              type="text" 
              [value]="kontrolltermin.monat_plan || '-'"
              disabled
              class="w-full px-2 py-1.5 text-sm border border-gray-300 rounded bg-gray-50 text-gray-500">
            <select
              *ngIf="isEditMode"
              formControlName="monat_plan"
              class="w-full px-2 py-1.5 text-sm border border-gray-300 rounded focus:ring-1 focus:ring-blue-500 focus:border-blue-500">
              <option value="">{{ 'APPOINTMENT_EDIT.SELECT_MONAT' | translate }}</option>
              <option *ngFor="let option of monatOptions" [value]="option.value">
                {{ option.label }}
              </option>
            </select>
          </div>

          <!-- Wochentag Plan -->
          <div>
            <label class="block text-xs font-medium text-gray-700 mb-1">{{ 'APPOINTMENT_EDIT.FIELD_WOCHENTAG_PLAN' | translate }}</label>
            <input 
              *ngIf="!isEditMode"
              type="text" 
              [value]="kontrolltermin.wochentag_plan || '-'"
              disabled
              class="w-full px-2 py-1.5 text-sm border border-gray-300 rounded bg-gray-50 text-gray-500">
            <select
              *ngIf="isEditMode"
              formControlName="wochentag_plan"
              class="w-full px-2 py-1.5 text-sm border border-gray-300 rounded focus:ring-1 focus:ring-blue-500 focus:border-blue-500">
              <option value="">{{ 'APPOINTMENT_EDIT.SELECT_WOCHENTAG' | translate }}</option>
              <option *ngFor="let option of wochentagOptions" [value]="option.value">
                {{ option.label }}
              </option>
            </select>
          </div>

          <!-- Soll-Zeit -->
          <div>
            <label class="block text-xs font-medium text-gray-700 mb-1">{{ 'APPOINTMENT_EDIT.FIELD_SOLL_ZEIT' | translate }}</label>
            <input 
              *ngIf="!isEditMode"
              type="text" 
              [value]="kontrolltermin.soll_zeit ? kontrolltermin.soll_zeit + ' Min' : '-'"
              disabled
              class="w-full px-2 py-1.5 text-sm border border-gray-300 rounded bg-gray-50 text-gray-500">
            <input
              *ngIf="isEditMode"
              type="number"
              formControlName="soll_zeit"
              placeholder="120"
              class="w-full px-2 py-1.5 text-sm border border-gray-300 rounded focus:ring-1 focus:ring-blue-500 focus:border-blue-500">
          </div>

          <!-- Ist-Zeit -->
          <div>
            <label class="block text-xs font-medium text-gray-700 mb-1">{{ 'APPOINTMENT_EDIT.FIELD_IST_ZEIT' | translate }}</label>
            <input 
              *ngIf="!isEditMode"
              type="text" 
              [value]="kontrolltermin.ist_zeit ? kontrolltermin.ist_zeit + ' Min' : '-'"
              disabled
              class="w-full px-2 py-1.5 text-sm border border-gray-300 rounded bg-gray-50 text-gray-500">
            <input
              *ngIf="isEditMode"
              type="number"
              formControlName="ist_zeit"
              placeholder="120"
              class="w-full px-2 py-1.5 text-sm border border-gray-300 rounded focus:ring-1 focus:ring-blue-500 focus:border-blue-500">
          </div>

          <!-- Verrechnete Viertel (billable quarters) -->
          <div>
            <label class="block text-xs font-medium text-gray-700 mb-1">Verrechnete Viertel</label>
            <input
              *ngIf="!isEditMode"
              type="text"
              [value]="kontrolltermin.verrechnete_viertel != null ? kontrolltermin.verrechnete_viertel : '-'"
              disabled
              class="w-full px-2 py-1.5 text-sm border border-gray-300 rounded bg-gray-50 text-gray-500">
            <select
              *ngIf="isEditMode"
              formControlName="verrechnete_viertel"
              class="w-full px-2 py-1.5 text-sm border border-gray-300 rounded focus:ring-1 focus:ring-blue-500 focus:border-blue-500">
              <option [ngValue]="null">-</option>
              <option *ngFor="let q of [1,2,3,4,5,6,7,8,9,10,11,12]" [ngValue]="q">{{ q }}</option>
            </select>
          </div>
        </div>

        <!-- Full Width Fields (moved to after indicators table for bearbeitung status) -->
        <div *ngIf="sourceStatus !== 'bearbeitung'" class="lg:col-span-2 grid grid-cols-1 md:grid-cols-2 gap-4">
          <!-- Anmerkungen -->
          <div>
            <label class="block text-xs font-medium text-gray-700 mb-1">{{ 'APPOINTMENT_EDIT.FIELD_NOTES' | translate }}</label>
            <div
              *ngIf="!isEditMode"
              class="w-full px-2 py-1.5 text-sm border border-gray-300 rounded bg-gray-50 text-gray-700 whitespace-pre-line break-words min-h-[2.5rem]">{{ kontrolltermin.anmerkungen || '-' }}</div>
            <textarea
              *ngIf="isEditMode"
              formControlName="anmerkungen"
              rows="4"
              [placeholder]="'APPOINTMENT_EDIT.PLACEHOLDER_NOTES' | translate"
              class="w-full px-2 py-1.5 text-sm border border-gray-300 rounded focus:ring-1 focus:ring-blue-500 focus:border-blue-500 resize-y"></textarea>
          </div>

          <!-- Zusatz-Maßnahme -->
          <div>
            <label class="block text-xs font-medium text-gray-700 mb-1">{{ 'APPOINTMENT_EDIT.FIELD_ADDITIONAL_MEASURE' | translate }}</label>
            <div
              *ngIf="!isEditMode"
              class="w-full px-2 py-1.5 text-sm border border-gray-300 rounded bg-gray-50 text-gray-700 whitespace-pre-line break-words min-h-[2.5rem]">{{ kontrolltermin.zusatz_massnahme || '-' }}</div>
            <textarea
              *ngIf="isEditMode"
              formControlName="zusatz_massnahme"
              rows="4"
              [placeholder]="'APPOINTMENT_EDIT.PLACEHOLDER_ADDITIONAL_MEASURE' | translate"
              class="w-full px-2 py-1.5 text-sm border border-gray-300 rounded focus:ring-1 focus:ring-blue-500 focus:border-blue-500 resize-y"></textarea>
          </div>
        </div>
      </form>
    </div> <!-- End of legacy header section -->



    </div> <!-- End of default view for other statuses -->
  </div> <!-- End of Content -->
</div> <!-- End of bg-white -->
</div> <!-- End of container -->
